import type { ReadOnlyProfilePermissions, Section } from "@qendo/permissions";
import { ProfileRole } from "@qendo/profile";
import type { ShareResponsePayload } from "@qendo/profile/invitation";
import * as Clipboard from "expo-clipboard";
import { useEffect, useId, useState } from "react";
import { View } from "react-native";
import { Dialog } from "react-native-paper";
import { Button } from "~/components/Button";
import { AlertModal } from "~/components/feedback/AlertModal";
import { CheckboxInput } from "~/components/form/CheckboxInput";
import { FieldGroup } from "~/components/form/FieldGroup";
import { TextArea } from "~/components/form/TextArea";
import { TextInput } from "~/components/form/TextInput";
import { PermissionEditor } from "~/components/form/team/PermissionEditor";
import { SelectFromContacts } from "~/components/form/team/SelectFromContacts";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { BodyText } from "~/components/layout/text";
import { Gap, Padding } from "~/constants/layout";
import { Permissions } from "~/entities/profile/permissions";
import { useErrorBag } from "~/providers/ErrorBagProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { empty, filled, isEmail } from "~/util";

import { makeSharePermissions } from "~/util/qendo/permissions";

export type ShareContentProps = {
    onRequestClose: () => void;
    initial?: Section[] | null;
} & Partial<FullPageModalProps>;

export const ShareContent = ({ onRequestClose, initial, ...modalProps }: ShareContentProps) => {
    const { cannot, createShare } = useProfile();
    const errorBag = useErrorBag();
    const { saving, setSaving } = useFeedback();
    const [email, setEmail] = useState<string>("");
    const [name, setName] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [permissions, setPermissions] = useState<ReadOnlyProfilePermissions>(makeSharePermissions(initial));
    const [consented, setConsented] = useState<boolean>(false);
    const [response, setResponse] = useState<ShareResponsePayload | null>(null);
    const id = useId();

    // biome-ignore lint/correctness/useExhaustiveDependencies(initial): read only
    // biome-ignore lint/correctness/useExhaustiveDependencies(errorBag.forget): shared value
    useEffect(() => {
        setResponse(null);
        setEmail("");
        setName("");
        setDescription("");
        setPermissions(makeSharePermissions(initial));
        setConsented(false);
        errorBag.forget(["email", "name", "consent_granted", "permissions"]);
    }, []);

    useEffect(() => {
        if (cannot("update", "team")) {
            onRequestClose();
        }
    }, [cannot, onRequestClose]);

    const can_save =
        !saving &&
        isEmail(email) &&
        filled(name) &&
        filled(new Permissions(permissions).onlyAllowed()) &&
        consented &&
        empty(response);

    const saveShare = () => {
        if (!can_save) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        createShare({
            email,
            name,
            description,
            permissions,
            consent_granted: consented,
        })
            .then((response) => {
                if (filled(response)) {
                    setResponse(response);
                } else {
                    onRequestClose();
                }
            })
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    const copy_message = () => {
        if (response) {
            Clipboard.setStringAsync(
                [
                    `Hello ${response.name},`,
                    "I would like to share some information from my QENDO app with you.",
                    `Please go to ${response.url} and use the password ${response.password} to access this information.`,
                ].join("\n\n"),
            );
        }
    };

    return (
        <>
            {response ? (
                <AlertModal
                    icon="share"
                    title="Profile shared"
                    description={`Your profile has been shared with ${response.name}`}
                    onDismiss={onRequestClose}
                >
                    <View style={[Gap.sm, Padding.lg, Padding.bottom_xl]}>
                        <BodyText>We have sent an email to {email}</BodyText>
                        <BodyText>You can also send them this link:</BodyText>
                        <BodyText colour="info">{response.url}</BodyText>
                        <BodyText>
                            And use the password: <BodyText colour="secondary">{response.password}</BodyText>
                        </BodyText>
                    </View>
                    <Dialog.Actions>
                        <Button title="Copy to clipboard" variant="secondary" onPress={copy_message} />
                        <Button title="Done" variant="primary" onPress={onRequestClose} />
                    </Dialog.Actions>
                </AlertModal>
            ) : (
                <FullPageModal
                    {...modalProps}
                    cancelable
                    onRequestClose={onRequestClose}
                    headerRight={() => (
                        <Button variant="secondary" title="Share" size="sm" disabled={!can_save} onPress={saveShare} />
                    )}
                >
                    <FieldGroup>
                        <TextInput
                            label="Name"
                            name="name"
                            value={name}
                            onChangeText={setName}
                            right={() => <SelectFromContacts setName={setName} setEmail={setEmail} />}
                        />
                        <TextInput
                            label="Email"
                            name="email"
                            value={email}
                            onChangeText={setEmail}
                            autoCapitalize={"none"}
                            textContentType="emailAddress"
                        />
                        <CheckboxInput
                            label="I consent to share this information"
                            value={consented}
                            onValueChange={setConsented}
                            name="consent_granted"
                            required
                        />
                        <TextArea
                            label="Message"
                            name="description"
                            value={description}
                            onChangeText={setDescription}
                            optional
                        />
                        <PermissionEditor
                            title="Content to share"
                            value={permissions}
                            onChangePermissions={setPermissions}
                            role={ProfileRole.share}
                            editing
                        />
                    </FieldGroup>
                </FullPageModal>
            )}
        </>
    );
};
